//! This file includes multiple resource classes and a converter to prevent circular references.
import {
    IPropertyConverter,
    JsonConverter,
    JsonElementType,
    JsonObject,
    JsonProperty,
    JsonType,
    JsonValueObject,
    TaJson,
} from "ta-json";

import type { RelationRoleType } from "../contracts/base/relation-role.js";
import type CultureInfo from "../culture-info.js";
import { EntityLink } from "../entity-link";
import type { ConditionalValidationOperator } from "./conditional-validation-operator.js";
import type { LogicalOperator } from "./logical-operator.js";
export class ConditionalValidationResourceConverter implements IPropertyConverter {
    serialize(value: ConditionalValidationResource): JsonValueObject {
        return TaJson.serialize(value) as JsonValueObject;
    }

    deserialize(value: JsonValueObject): ConditionalValidationResource {
        if (value["relation_name"]) {
            return TaJson.deserialize<RelationMemberConditionalValidationResource>(
                value,
                RelationMemberConditionalValidationResource
            );
        } else if (value["property_name"]) {
            return TaJson.deserialize<PropertyMemberConditionalValidationResource>(
                value,
                PropertyMemberConditionalValidationResource
            );
        } else if (Array.isArray(value.subFilters)) {
            return new LogicalMemberConditionalValidationResource({
                operator: value.operator as LogicalOperator,
                subFilters: (value.subFilters as Array<JsonValueObject>).map(filter => this.deserialize(filter)),
            });
        } else {
            throw Error(
                `Unable to deserialize to ConditionalValidationResource. Value has unknown format:\n${JSON.stringify(
                    value,
                    undefined,
                    2
                )}`
            );
        }
    }
}

export abstract class ConditionalValidationResource {}

@JsonObject()
export class LogicalMemberConditionalValidationResource extends ConditionalValidationResource {
    constructor(init?: Partial<LogicalMemberConditionalValidationResource>) {
        super();
        Object.assign(this, init);
    }

    @JsonProperty("operator")
    operator: LogicalOperator;

    @JsonProperty("subFilters")
    @JsonConverter(ConditionalValidationResourceConverter)
    @JsonType(Array)
    subFilters: Array<ConditionalValidationResource>;
}

@JsonObject()
export class PropertyMemberConditionalValidationResource extends ConditionalValidationResource {
    constructor(init?: Partial<PropertyMemberConditionalValidationResource>) {
        super();
        Object.assign(this, init ?? {});
    }

    @JsonProperty("operator")
    operator: ConditionalValidationOperator;

    @JsonProperty("property_name")
    propertyName?: string;

    @JsonProperty("culture")
    culture?: CultureInfo;

    @JsonProperty("subFilters")
    @JsonType(Array)
    values: Array<unknown>;
}

@JsonObject()
export class RelationMemberConditionalValidationResource extends ConditionalValidationResource {
    constructor(init?: Partial<RelationMemberConditionalValidationResource>) {
        super();
        Object.assign(this, init ?? {});
    }

    @JsonProperty("operator")
    operator: ConditionalValidationOperator;

    @JsonProperty("relation_name")
    relationName?: string;

    @JsonProperty("relation_role")
    relationRole?: RelationRoleType;

    @JsonProperty("subFilters")
    @JsonElementType(EntityLink)
    values: Array<EntityLink>;
}
